import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  andAllWhere,
  andWhere,
  buildQuery,
  CombinedFilter,
  FilterFn,
  noop,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  select,
  where,

} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient } from "@angular/common/http";
import {
  IPagination,
  IPaginationResult,
  IRequestResult,
} from "../../../../common/interfaces";
import { environment as env } from "../../../../../environments/environment.prod";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { AbstractService } from "app/services/abstract.service";
import { ICreateEmissionPoint } from "./interfaces/create-emission-point.interface";
import { IUpdateEmissionPoint } from "./interfaces/update-emission-point.interface";
import EmissionPointAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/documents/emission-point/emission-point.entity";
import { RequestOptions } from "app/common/interfaces/options.interface";
const apiUrl = env.API_DOCUMENT_URL;
const moduleName = "emission_point";

@Injectable({
  providedIn: "root",
})
export class EmissionPointService extends AbstractService<
  EmissionPointAttributes,
  ICreateEmissionPoint,
  IUpdateEmissionPoint
> {

  /** The last filter used to load the list */
  currentFilter: CombinedFilter<EmissionPointAttributes>;

  constructor(
    http: HttpClient,
  ) {
    super(apiUrl, moduleName, http);
  }

  /**
   * Obtiene puntos de emisión con paginación y filtros.
   *
   * Estrategia de búsqueda:
   * - Sin búsqueda: Un solo objeto con filtros base
   * - Con búsqueda: Tres objetos (OR lógico) - busca en 'name', 'subRoute.name', 'subRoute.externalKeys.code'
   *
   * Cada objeto del OR debe incluir TODAS las condiciones base para mantener consistencia.
   *
   * @param options Pagination options
   * @param companyId Company filter
   * @param typeEmissionPointId Type emission point filter
   * @param status Status filter
   * @param subRouteId Sub route filter
   * @returns Paginated data
   *
   * Search fields: name, subRoute.name, subRoute.externalKeys.code
   * Array filters: typeEmissionPointId, subRouteId, status
   * Single filters: companyId
   */
  findAllWithCount(
    options: IPagination<EmissionPointAttributes>,
    companyId?,
    typeEmissionPointId?,
    status?: number[],
    subRouteId?,
  ): Observable<IPaginationResult<EmissionPointAttributes>> {

    const hasSearch = !!options.search;

    /**
     * Construye las condiciones base que SIEMPRE deben aplicarse.
     * Incluye: _deleted=false, filtro de compañía, tipo, ruta, estados.
     *
     * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
     */
    const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<EmissionPointAttributes>[] => {
      const deletedFilter: FilterFn<EmissionPointAttributes> = params.useOrWhere
        ? orWhere('_deleted', Ops.eq(false))  // Inicia nuevo objeto en el array
        : where('_deleted', Ops.eq(false));   // Primer objeto del array

      return [
        deletedFilter,
        companyId
          ? andWhere('company.id' as keyof EmissionPointAttributes, Ops.eq(companyId))
          : noop(),
        typeEmissionPointId && typeEmissionPointId.length > 0
          ? andWhere('type' as keyof EmissionPointAttributes, Ops.in(...typeEmissionPointId))
          : noop(),
        subRouteId && subRouteId.length > 0
          ? andWhere('subRoute.id' as keyof EmissionPointAttributes, Ops.in(...subRouteId))
          : noop(),
        status && status.length > 0
          ? andWhere('statusId', Ops.in(...status))
          : noop()
      ];
    };

    // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

    // Objeto 1: Condiciones base + búsqueda por NAME (o solo condiciones si no hay búsqueda)
    const searchByNameQueryObject: FilterFn<EmissionPointAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }),  // false = usa 'where' (primer objeto)
      hasSearch ? andWhere("name", Ops.like(options.search!, "i")) : noop()
    ];

    // Objeto 2: Condiciones base + búsqueda por SUB ROUTE NAME (solo si hay búsqueda)
    const searchBySubRouteNameQueryObject: FilterFn<EmissionPointAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (segundo objeto)
      andWhere("subRoute.name" as keyof EmissionPointAttributes, Ops.like(options.search!, "i"))
    ] : [];

    // Objeto 3: Condiciones base + búsqueda por SUB ROUTE EXTERNAL KEYS CODE (solo si hay búsqueda)
    const searchBySubRouteCodeQueryObject: FilterFn<EmissionPointAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),
      andWhere("subRoute.externalKeys.code" as keyof EmissionPointAttributes, Ops.like(options.search!, "i"))
    ] : [];

    // ========== ENSAMBLAJE FINAL ==========

    const query = buildQuery<EmissionPointAttributes>(
      // Expandimos los tres objetos en el buildQuery
      ...searchByNameQueryObject,
      ...searchBySubRouteNameQueryObject,
      ...searchBySubRouteCodeQueryObject,

      // Ordenamiento y paginación
      orderBy(
        options.sort ?? "name",
        options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    this.currentFilter = query;

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<EmissionPointAttributes>>(API_URL);
  }

  /**
   * Find to combo box
   * @returns Data
   */
  findToComboBox(emmisionPointIds?): Observable<IRequestResult<EmissionPointAttributes[]>> {
    const query = buildQuery<EmissionPointAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      emmisionPointIds && Array.isArray(emmisionPointIds) ? andAllWhere("_id", Ops.in(...emmisionPointIds)) : noop(),
      select(["id", "name", "company"]),
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<EmissionPointAttributes[]>>(API_URL);
  }

  findByFilter(filter: CombinedFilter<EmissionPointAttributes>, options?: RequestOptions) {
    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    const headers: Record<string, any> = {};
    // see order-customer-export for more info about this header
    // ctx: stands for context
    if (options?.contextHeader) headers['x-ave-ctx'] = options.contextHeader;
    return this.http.get<IPaginationResult<EmissionPointAttributes>>(API_URL, { headers });
  }
}
